/**
 * PENDING UPGRADE INTENT
 * ======================
 *
 * A signed-out visitor who clicks "Start Annual Plan" on the landing page
 * cannot be sent to Stripe: Checkout needs an account to attach the plan to.
 * They are sent to sign-up instead, and without this the plan they picked is
 * forgotten — they land on the dashboard on the free plan with no indication
 * that anything was meant to happen.
 *
 * The chosen plan is parked here across the sign-up round trip. App.tsx reads
 * it once the user is authenticated and opens the pricing page, where Pricing
 * consumes it and starts checkout.
 *
 * sessionStorage, not localStorage: the intent belongs to this visit. A plan
 * picked weeks ago should not silently open Stripe on some later one. Every
 * access is wrapped because storage throws in a private window and on a
 * blocked-cookies setting, where losing the intent must not break the page.
 */

const KEY = 'decisionr.pendingUpgradePlan';

export type PendingPlan = 'annual' | 'lifetime';

function isPendingPlan(value: unknown): value is PendingPlan {
  return value === 'annual' || value === 'lifetime';
}

export function setPendingUpgrade(plan: PendingPlan): void {
  try {
    window.sessionStorage.setItem(KEY, plan);
  } catch {
    // No storage: the user finishes sign-up on the free plan and upgrades from
    // the dashboard. Worth nothing more than losing the shortcut.
  }
}

export function peekPendingUpgrade(): PendingPlan | null {
  try {
    const value = window.sessionStorage.getItem(KEY);
    return isPendingPlan(value) ? value : null;
  } catch {
    return null;
  }
}

/** Read and clear, so a failed checkout does not reopen Stripe on every render. */
export function takePendingUpgrade(): PendingPlan | null {
  const plan = peekPendingUpgrade();
  clearPendingUpgrade();
  return plan;
}

export function clearPendingUpgrade(): void {
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    // Nothing was stored if the write failed too.
  }
}
